/**
 * 프로젝트(채널) 손익 상세 — 재무 표에서 프로젝트명 클릭 시 호출.
 */
import React, { useEffect, useState } from "react";
import {
    Box, Chip, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { ds } from "../../../styles/designSystem";

import { financeApi, fmtKRWFull } from "../api/financeApi";
import DetailModal from "../../../component/common/DetailModal";
import LoadingDots from "../../../component/common/LoadingDots";
import ResizableTable from "../../../component/common/ResizableTable";

export default function ProjectDetailDialog({ open, channelId, onClose }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open || !channelId) return;
        setLoading(true);
        financeApi.getProject(channelId)
            .then(res => setData(res?.data ?? res ?? null))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [open, channelId]);

    return (
        <DetailModal
            open={open}
            onClose={onClose}
            title={data?.channelName || "프로젝트 상세"}
            subtitle={data?.deadlineDate
                ? (data.deadlineExpired ? "마감" : `마감 ~ ${data.deadlineDate}`)
                : undefined}
            maxWidth="md"
        >
            {loading || !data ? (
                <Box sx={{ p: 4, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    {loading ? <LoadingDots/> : "프로젝트 정보를 찾을 수 없습니다."}
                </Box>
            ) : (
                <>
                    {/* 요약 KPI */}
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 1.5, mb: 2.5,
                    }}>
                        <Kpi label="총 수입"   value={fmtKRWFull(data.totalIncome  || 0)} color="#16a34a" />
                        <Kpi label="총 지출"   value={fmtKRWFull(data.totalExpense || 0)} color="#dc2626" />
                        <Kpi label="순이익"    value={fmtKRWFull(data.netProfit    || 0)}
                            color={(data.netProfit || 0) >= 0 ? "#2563eb" : "#dc2626"} />
                    </Box>

                    <Section title={`거래 내역 (${(data.transactions || []).length})`}>
                        {(data.transactions || []).length === 0 ? (
                            <EmptyHint>등록된 거래가 없습니다.</EmptyHint>
                        ) : (
                            <ResizableTable size="small" sx={ds.tableMiniSx}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>일자</TableCell>
                                        <TableCell>항목</TableCell>
                                        <TableCell>거래처</TableCell>
                                        <TableCell>구분</TableCell>
                                        <TableCell align="right">금액</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.transactions.map(t => (
                                        <TableRow key={t.id}>
                                            <TableCell align="center" sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{t.transactionDate}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12, fontWeight: 600 }}>{t.title}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12, color: "#64748b" }}>{t.vendorName || "—"}</TableCell>
                                            <TableCell align="center">
                                                <Chip label={t.typeLabel || t.type} size="small" sx={{
                                                    height: 18, fontSize: 10, fontWeight: 700,
                                                    bgcolor: t.type === "INCOME" ? "#f0fdf4" : "#fef2f2",
                                                    color:   t.type === "INCOME" ? "#16a34a" : "#dc2626",
                                                }} />
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                                                color: t.type === "INCOME" ? "#16a34a" : "#dc2626",
                                            }}>
                                                {t.type === "INCOME" ? "+" : "-"}{fmtKRWFull(t.amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </ResizableTable>
                        )}
                    </Section>

                    {(data.approvalLinks || []).length > 0 && (
                        <Section title={`결재 연동 (${data.approvalLinks.length})`}>
                            <Stack spacing={0.6}>
                                {data.approvalLinks.map(l => (
                                    <Box key={l.id} sx={{
                                        p: 1, borderRadius: 1, border: "1px solid #eef2f6",
                                        fontSize: 12,
                                    }}>
                                        <b>{l.title}</b>
                                        <Box component="span" sx={{ color: "#94a3b8", ml: 1 }}>
                                            {l.applicantName} · {l.amount ? fmtKRWFull(l.amount) : ""}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Section>
                    )}
                </>
            )}
        </DetailModal>
    );
}

function Section({ title, children }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f172a", mb: 0.7 }}>{title}</Typography>
            <Divider sx={{ mb: 1, borderColor: "#eef2f6" }} />
            {children}
        </Box>
    );
}

function Kpi({ label, value, color }) {
    return (
        <Box sx={{ p: 1.4, border: "1px solid #eef2f6", borderRadius: 1.2 }}>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color, mt: 0.3 }}>{value}</Typography>
        </Box>
    );
}

function EmptyHint({ children }) {
    return (
        <Box sx={{ p: 3, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>{children}</Box>
    );
}
